import { useState } from 'react';
import { AppLink } from '../components/AppLink';
import { BeforeAfter } from '../components/BeforeAfter';
import { ConsultationForm } from '../components/ConsultationForm';
import { Icon } from '../components/Icon';
import type { IconName } from '../components/icons.generated';
import { Picture } from '../components/Picture';
import { articles, formatDate } from '../data/articles';
import { projects, type ProjectCategory } from '../data/projects';
import { services } from '../data/services';
import { Seo } from '../lib/seo';
import { SITE } from '../lib/site';
import { container, eyebrow, h2Section } from '../lib/ui';

const PILLARS: Array<{ icon: IconName; title: string; text: string }> = [
  {
    icon: 'architecture',
    title: 'Interior Expertise',
    text: 'Our collective of registered architects and master stylists brings forward-thinking creativity and technical mastery tailored to your lifestyle.',
  },
  {
    icon: 'diamond',
    title: 'Premium Quality',
    text: 'Sourced European veneers, engineered hardwoods, Italian marbles, and hand-finished coatings engineered for generational longevity.',
  },
  {
    icon: 'payments',
    title: 'Affordable Transparency',
    text: 'Exceptional design solutions with structured bill-of-materials and zero hidden margins, maximizing value across all square footage.',
  },
  {
    icon: 'forum',
    title: 'Free Consultation',
    text: 'Initiate your design journey with a zero-obligation, one-on-one session with our senior architectural lead to map possibilities.',
  },
  {
    icon: 'support_agent',
    title: '24 / 7 Client Support',
    text: 'Real-time project updates via our concierge WhatsApp line and dedicated site managers ensuring steady milestones.',
  },
  {
    icon: 'verified_user',
    title: 'Guaranteed Works',
    text: 'Comprehensive post-handover warranty and meticulous quality assurance checklists safeguarding every square inch.',
  },
];

const FILTERS: Array<{ value: 'all' | ProjectCategory; label: string }> = [
  { value: 'all', label: 'All Projects' },
  { value: 'residential', label: 'Residential' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'office', label: 'Office' },
  { value: 'commercial', label: 'Commercial' },
];

const homeProjects = projects.filter((p) => p.featuredOnHome);
const homeArticles = articles.filter((a) => a.featuredOnHome);
const homeServices = services.filter((s) => s.homeCard);

export default function Home() {
  const [filter, setFilter] = useState<'all' | ProjectCategory>('all');
  const visibleProjects = homeProjects.filter((p) => filter === 'all' || p.categories.includes(filter));

  return (
    <>
      <Seo
        title="Interior Designers in Nagercoil"
        description="Green Valley Interiors designs luxury homes, villas, offices and restaurants across Nagercoil, Kanyakumari and Trivandrum. Book a free design consultation."
        keywords={[
          'luxury interior designers in Nagercoil',
          'home interior design in Kanyakumari district',
          'villa interior designers in Tamil Nadu',
          'modular kitchen designers in Nagercoil',
          'office interior design company in Nagercoil',
          'turnkey interior contractors near Trivandrum',
        ]}
        path="/"
      />

      {/* HERO */}
      <section className="relative w-full -mt-20 overflow-hidden bg-botanical-dark text-on-primary">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary-container/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-[100px] pointer-events-none" />
        <div className={`relative ${container} pt-32 md:pt-36 pb-space-xl flex flex-col justify-between lg:min-h-[942px]`}>
          <div className="flex flex-wrap items-center justify-between gap-space-sm border-b border-border-dark-tint/70 pb-space-md">
            <div className="flex items-center gap-space-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-vivid animate-pulse" />
              <span className="font-label-md text-label-md uppercase tracking-[0.2em] text-gold-light">
                Atelier of Architectural Harmony • Est. Nagercoil
              </span>
            </div>
            <div className="hidden md:flex items-center gap-space-md text-gold-light/70 font-label-sm text-label-sm uppercase tracking-widest">
              <span>Residential</span>
              <span aria-hidden="true">—</span>
              <span>Executive Workspace</span>
              <span aria-hidden="true">—</span>
              <span>Commercial Havens</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center my-auto py-space-xl lg:py-space-lg">
            <div className="lg:col-span-7 flex flex-col gap-space-md z-10">
              <h1 className="flex flex-col gap-space-md animate-rise">
                <span className="inline-flex items-center gap-space-xs px-space-sm py-1 rounded bg-primary-container/80 border border-gold-dark/30 max-w-fit">
                  <Icon name="star_shine" size={15} className="text-gold-light" />
                  <span className="font-label-sm text-label-sm uppercase text-gold-light tracking-widest">
                    Luxury Interior Designers in Nagercoil
                  </span>
                </span>
                <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-surface-ivory tracking-tight !leading-[1.1]">
                  BRING MAGIC <br />
                  <span className="italic font-normal text-gold-light">TO HOME.</span>
                </span>
              </h1>
              <p className="font-body-xl text-body-lg md:text-body-xl text-surface-ivory/80 max-w-xl font-light animate-rise [animation-delay:80ms]">
                Inspiring spaces, inspired living. Bespoke luxury architecture, refined residential sanctuaries, and
                visionary commercial interiors crafted with meticulous organic poise.
              </p>
              <div className="flex flex-wrap items-center gap-space-md pt-space-sm">
                <AppLink
                  href="/portfolio"
                  className="group relative inline-flex items-center gap-space-sm bg-gold-light text-primary font-label-lg text-label-lg uppercase tracking-wider px-space-lg py-space-md rounded shadow-[0_10px_30px_rgba(229,212,190,0.25)] hover:bg-surface-ivory hover:shadow-[0_15px_40px_rgba(229,212,190,0.4)] transition-all duration-300"
                >
                  <span>Explore Projects</span>
                  <Icon name="arrow_forward" size={18} className="group-hover:translate-x-1 transition-transform" />
                </AppLink>
                <a
                  href="#consultation"
                  className="inline-flex items-center gap-space-sm bg-primary-container/60 hover:bg-primary-container border border-gold-light/40 text-surface-ivory font-label-lg text-label-lg uppercase tracking-wider px-space-lg py-space-md rounded backdrop-blur-md transition-all duration-300"
                >
                  <Icon name="calendar_today" size={18} className="text-gold-light" />
                  <span>Book Free Consultation</span>
                </a>
              </div>
              <dl className="grid grid-cols-3 gap-space-md pt-space-lg border-t border-border-dark-tint/60 mt-space-sm max-w-lg">
                {[
                  ['150+', 'Sanctuaries Built'],
                  ['100%', 'Craft Guarantee'],
                  ['24/7', 'Dedicated Atelier'],
                ].map(([value, label]) => (
                  <div key={label} className="flex flex-col-reverse justify-end">
                    <dt className="font-label-sm text-label-sm uppercase text-surface-ivory/70 tracking-wider">{label}</dt>
                    <dd className="font-headline-md text-headline-md text-gold-light font-semibold">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="lg:col-span-5 relative mt-space-lg lg:mt-0 sm:mb-12 lg:mb-0">
              <div className="relative rounded-lg overflow-hidden shadow-2xl bg-primary-container border border-gold-dark/20 aspect-[4/5] w-full">
                <Picture
                  name="home-hero-living-room"
                  alt="Double-height modern living room with fluted wood wall panels, brass lighting and emerald accents"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  priority
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark via-transparent to-transparent opacity-80" />
                <div className="absolute bottom-6 left-6 right-6 p-space-md bg-botanical-dark/85 backdrop-blur-md rounded border border-gold-light/20 flex items-center justify-between gap-space-sm">
                  <div>
                    <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">Signature Villa Series</span>
                    <p className="font-headline-sm text-headline-sm text-surface-ivory">Nagercoil Presidential Penthouse</p>
                  </div>
                  <AppLink
                    href="/portfolio"
                    aria-label="View the portfolio"
                    className="w-10 h-10 shrink-0 rounded-full bg-gold-light/20 hover:bg-gold-light text-surface-ivory hover:text-primary flex items-center justify-center transition-all"
                  >
                    <Icon name="north_east" size={18} />
                  </AppLink>
                </div>
              </div>
              <div className="absolute -bottom-16 -left-4 lg:-left-8 hidden sm:flex items-center gap-space-sm p-space-md bg-surface-ivory/90 text-on-surface backdrop-blur-xl rounded-lg shadow-[0_20px_40px_rgba(0,0,0,0.3)] border border-gold-light/40 z-20 max-w-xs animate-float">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <Icon name="verified" size={20} className="text-gold-light" />
                </div>
                <div>
                  <p className="font-label-md text-label-md text-primary font-bold uppercase tracking-wider">Certified Precision</p>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">Turnkey Execution &amp; High-End Joinery</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-space-md flex flex-wrap items-center justify-between gap-2 text-surface-ivory/60 font-body-sm text-body-sm">
            <span>Architectural Consultation • Space Optimization • Artisanal Millwork</span>
            <span className="hidden md:inline font-mono text-[12px] tracking-widest text-gold-light">
              LAT {SITE.geo.lat}° N / LONG {SITE.geo.lng}° E
            </span>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="w-full bg-surface-cream py-space-xl md:py-20 relative overflow-hidden">
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-lg">
            <div>
              <span className={eyebrow}>Spatial Metamorphosis</span>
              <h2 className={`${h2Section} mt-space-xs`}>From Bare Concrete to Sanctuary.</h2>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-md">
              Drag the interactive slider below to reveal how Green Valley orchestrates empty floor plans into cinematic,
              warm, functional masterworks.
            </p>
          </div>
          <BeforeAfter />
          <div className="flex items-center justify-between text-on-surface-variant font-label-sm text-label-sm uppercase tracking-widest max-w-5xl mx-auto mt-space-sm px-2 gap-2">
            <span className="flex items-center gap-1">
              <Icon name="west" size={16} /> Raw Structure Phase
            </span>
            <span className="hidden sm:inline text-secondary font-bold">Interactive Revelation</span>
            <span className="flex items-center gap-1 text-right">
              Finished Turnkey Interior <Icon name="east" size={16} />
            </span>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="w-full bg-surface py-space-xl md:py-20" id="services">
        <div className={container}>
          <div className="reveal max-w-2xl mb-space-xl">
            <span className={eyebrow}>Our Curated Practices</span>
            <h2 className={`${h2Section} mt-space-xs`}>Comprehensive Architectural Interior Solutions</h2>
            <p className="font-body-lg text-body-lg text-on-surface-variant mt-space-xs">
              Every space has an emotional timbre. We blend technical engineering with tailored tactile finishes to elevate
              everyday life.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {homeServices.map((s) => {
              const card = s.homeCard!;
              return (
                <article
                  key={s.slug}
                  className="reveal group flex flex-col bg-surface-ivory rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="relative h-72 overflow-hidden bg-surface-container">
                    <Picture
                      name={card.image}
                      alt={card.imageAlt}
                      sizes="(min-width: 768px) 33vw, 100vw"
                      className="block w-full h-full"
                      imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 bg-surface-ivory/90 backdrop-blur rounded font-label-sm text-label-sm uppercase text-primary tracking-widest">
                      {card.badge}
                    </div>
                  </div>
                  <div className="p-space-lg flex flex-col justify-between flex-1">
                    <div>
                      <div className="flex items-center justify-between mb-space-xs">
                        <h3 className="font-headline-sm text-headline-sm text-primary">{card.title}</h3>
                        <Icon name={s.icon} size={24} className="text-secondary" />
                      </div>
                      <p className="font-body-md text-body-md text-on-surface-variant mb-space-md">{card.description}</p>
                      <ul className="flex flex-col gap-1 text-on-surface font-body-sm text-body-sm mb-space-md">
                        {card.highlights.map((h) => (
                          <li key={h} className="flex items-center gap-2">
                            <Icon name="check" size={16} className="text-emerald-vivid" /> {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <AppLink
                      href={`/services/${s.slug}`}
                      className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-widest text-primary font-bold group-hover:text-secondary transition-colors"
                    >
                      <span>
                        Explore {card.title}
                        <span className="sr-only"> services</span>
                      </span>
                      <Icon name="arrow_forward" size={16} className="group-hover:translate-x-1 transition-transform" />
                    </AppLink>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="w-full bg-botanical-dark text-on-primary py-space-xl md:py-20 relative">
        <div className={`${container} relative z-10`}>
          <div className="reveal text-center max-w-3xl mx-auto mb-space-xl">
            <span className="font-label-md text-label-md uppercase tracking-[0.25em] text-gold-light">The Green Valley Standards</span>
            <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-ivory mt-space-xs">
              Why Discerning Clients Trust Us
            </h2>
            <p className="font-body-lg text-body-lg text-surface-ivory/70 mt-space-xs">
              From concept sketches to custom brass joinery installation, we anchor every engagement on uncompromised
              artistry and transparency.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="reveal p-space-lg rounded-xl bg-primary-container/40 backdrop-blur-md border border-gold-light/20 hover:border-gold-light/60 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded bg-gold-light/10 text-gold-light flex items-center justify-center mb-space-md">
                  <Icon name={p.icon} size={28} />
                </div>
                <h3 className="font-headline-sm text-headline-sm text-surface-ivory mb-space-xs">{p.title}</h3>
                <p className="font-body-md text-body-md text-surface-ivory/75">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="w-full bg-surface-cream py-space-xl md:py-20" id="portfolio">
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-lg">
            <div>
              <span className={eyebrow}>Curated Archive</span>
              <h2 className={`${h2Section} mt-space-xs`}>Distinguished Case Studies</h2>
            </div>
            <div className="flex flex-wrap items-center gap-space-xs" role="group" aria-label="Filter case studies">
              {FILTERS.map((f) => {
                const active = filter === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter(f.value)}
                    className={`px-4 py-2 rounded-pill font-label-sm text-label-sm uppercase tracking-wider transition-all ${
                      active ? 'bg-primary text-gold-light' : 'bg-surface text-on-surface hover:bg-gold-light/30'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {visibleProjects.map((p) => (
              <AppLink
                key={p.slug}
                href={`/portfolio/${p.slug}`}
                className="group relative block overflow-hidden rounded-lg bg-surface shadow-sm hover:shadow-xl transition-all duration-500"
              >
                <div className="relative h-80 overflow-hidden">
                  <Picture
                    name={p.image}
                    alt={p.imageAlt}
                    sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/90 via-botanical-dark/20 to-transparent" />
                  <div className="absolute top-4 left-4 px-3 py-1 rounded bg-surface-ivory/80 backdrop-blur font-label-sm text-label-sm uppercase tracking-wider text-primary">
                    {p.tag}
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="font-headline-sm text-headline-sm text-surface-ivory">{p.title}</h3>
                    <p className="font-body-sm text-body-sm text-gold-light/90 mt-1">{p.summary}</p>
                  </div>
                </div>
              </AppLink>
            ))}
          </div>
          <div className="mt-space-lg flex justify-center">
            <AppLink
              href="/portfolio"
              className="inline-flex items-center gap-1 font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary transition-colors"
            >
              View the full portfolio <Icon name="arrow_forward" size={16} />
            </AppLink>
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="w-full bg-surface py-space-xl md:py-20">
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-lg">
            <div>
              <span className={eyebrow}>The Atelier Journal</span>
              <h2 className={`${h2Section} mt-space-xs`}>Perspectives on Modern Living</h2>
            </div>
            <AppLink
              href="/journal"
              className="font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary flex items-center gap-1 transition-colors"
            >
              <span>Read All Essays</span>
              <Icon name="arrow_forward" size={16} />
            </AppLink>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {homeArticles.map((a) => (
              <article
                key={a.slug}
                className="reveal relative flex flex-col group bg-surface-ivory rounded-lg p-space-md shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="relative h-56 rounded overflow-hidden mb-space-md">
                  <Picture
                    name={a.homeImage ?? a.image}
                    alt={a.imageAlt}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <time
                    dateTime={a.date}
                    className="absolute top-3 right-3 px-2.5 py-1 rounded bg-primary text-gold-light font-label-sm text-label-sm"
                  >
                    {formatDate(a.date, false)}
                  </time>
                </div>
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-secondary">{a.category}</span>
                <h3 className="font-headline-sm text-headline-sm text-primary mt-1 mb-2 group-hover:text-secondary transition-colors">
                  <AppLink href={`/journal/${a.slug}`} className="after:absolute after:inset-0">
                    {a.shortTitle}
                  </AppLink>
                </h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant line-clamp-3">{a.excerpt}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULTATION */}
      <section className="w-full bg-botanical-dark text-on-primary py-space-xl md:py-20 relative overflow-hidden" id="consultation">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-container/30 rounded-full blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
            <div className="lg:col-span-5 flex flex-col gap-space-md">
              <div className="inline-flex items-center gap-space-xs px-space-sm py-1 rounded bg-gold-light/10 text-gold-light max-w-fit">
                <span className="font-label-sm text-label-sm uppercase tracking-widest">Tailored Engagement</span>
              </div>
              <h2 className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-surface-ivory !leading-tight">
                Begin Your Spatial <br />
                <span className="italic text-gold-light">Transformation.</span>
              </h2>
              <p className="font-body-lg text-body-lg text-surface-ivory/80">
                Share your blueprint or dream spatial layout. Our principal architects will review your parameters and
                prepare a bespoke preliminary plan.
              </p>
              <div className="flex flex-col gap-space-sm pt-space-xs">
                <a
                  className="flex items-center justify-between p-space-md rounded-lg bg-primary-container/80 hover:bg-primary-container border border-gold-light/30 transition-all duration-300 group"
                  href={SITE.whatsappUrl}
                  rel="noopener"
                  target="_blank"
                >
                  <div className="flex items-center gap-space-sm">
                    <div className="w-10 h-10 rounded-full bg-emerald-vivid text-on-primary flex items-center justify-center">
                      <Icon name="chat" size={20} />
                    </div>
                    <div>
                      <div className="font-label-sm text-label-sm text-gold-light uppercase tracking-wider">WhatsApp Concierge</div>
                      <div className="font-headline-sm text-headline-sm text-surface-ivory">{SITE.phoneDisplay}</div>
                    </div>
                  </div>
                  <Icon name="arrow_forward" size={22} className="text-gold-light group-hover:translate-x-1 transition-transform" />
                </a>
                <div className="flex items-start gap-space-sm p-space-md rounded-lg bg-surface-cream/5 border border-white/10">
                  <Icon name="apartment" size={22} className="text-gold-light mt-0.5" />
                  <div>
                    <span className="font-label-sm text-label-sm text-gold-light uppercase tracking-wider block">Headquarters</span>
                    <p className="font-body-sm text-body-sm text-surface-ivory/80 mt-1">
                      {SITE.address.line1}, {SITE.address.line2}, {SITE.address.locality}, {SITE.address.region} •{' '}
                      <a href={`mailto:${SITE.email}`} className="underline decoration-gold-light/40 hover:text-gold-light">
                        {SITE.email}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ConsultationForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
